import React from 'react';
import { motion } from 'framer-motion';
import { Download, Star, Zap, TrendingUp, Shield, Clock } from 'lucide-react';

const Hero = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-b from-white to-green-50">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-72 h-72 bg-primary/20 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-accent/15 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-primary/10 rounded-full blur-2xl animate-glow"></div>
        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(76,175,80,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(76,175,80,0.05)_1px,transparent_1px)] bg-[size:50px_50px]"></div>
      </div>

      <div className="relative z-10 w-full max-w-[85%] mx-auto px-4 md:px-6 pt-28 pb-12 md:pt-24 md:pb-16 lg:pt-20 lg:pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
          {/* Left Content */}
          <motion.div
            className="text-center lg:text-left"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Logo */}
            <motion.div
              className="mb-6 md:mb-8"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="inline-flex items-center gap-4">
                <div className="relative">
                  <img
                    src="/logo.png"
                    alt="SMR Online Logo"
                    className="h-16 w-16 md:h-20 md:w-20 rounded-2xl shadow-lg green-glow"
                    loading="eager"
                    decoding="async"
                  />
                </div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gradient">
                  SMR Online
                </h1>
              </div>
            </motion.div>

            {/* Tagline */}
            <motion.h2
              className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 md:mb-6 leading-tight text-gray-900"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Play, Win &{' '}
              <span className="text-gradient gradient-text-animated">
                Earn
              </span>
            </motion.h2>

            <motion.p
              className="text-base md:text-lg lg:text-xl text-gray-600 mb-6 md:mb-8 max-w-lg mx-auto lg:mx-0"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              Download India's No. 1 Matka App. Get fastest live results, guessing forums, and instant withdrawals.
            </motion.p>

            {/* Feature Pills */}
            <motion.div
              className="flex flex-wrap gap-3 justify-center lg:justify-start mb-6 md:mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
            >
              {[
                { icon: Zap, text: 'Fast Results' },
                { icon: Shield, text: 'Secure' },
                { icon: TrendingUp, text: 'Best Rates' }
              ].map((item, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 bg-white border border-green-200 shadow-sm px-4 py-2 rounded-full text-sm"
                >
                  <item.icon className="w-4 h-4 text-primary" />
                  <span className="text-gray-700">{item.text}</span>
                </div>
              ))}
            </motion.div>

            {/* Download Button */}
            <motion.div
              className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center lg:justify-start mb-6 md:mb-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <a
                href="https://www.smronline.org/smronline.apk"
                download
                className="group bg-gradient-primary text-white px-8 md:px-10 py-4 md:py-5 rounded-full font-semibold text-base md:text-lg hover:shadow-lg hover:shadow-primary/40 transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-3 pulse-glow"
              >
                <Download className="w-5 h-5 md:w-6 md:h-6" />
                <span>Download Now</span>
              </a>
            </motion.div>

            {/* Stats */}
            <motion.div
              className="flex flex-wrap gap-6 md:gap-8 justify-center lg:justify-start"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1 }}
            >
              {[
                { value: '10K+', label: 'Active Users' },
                { value: '24/7', label: 'Support' },
                { value: '4.8★', label: 'Rating' }
              ].map((stat, index) => (
                <div key={index} className="text-center lg:text-left">
                  <div className="text-2xl md:text-3xl font-bold text-primary">{stat.value}</div>
                  <div className="text-sm text-gray-500">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right Content - Phone Mockup */}
          <motion.div
            className="flex justify-center lg:justify-end"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <div className="relative">
              {/* Glow Effect */}
              <div className="absolute -inset-4 bg-gradient-radial from-primary/20 via-primary/5 to-transparent rounded-[3rem] blur-2xl animate-pulse"></div>

              {/* Phone Frame */}
              <div className="relative w-64 h-[500px] md:w-72 md:h-[560px] lg:w-80 lg:h-[620px] bg-gradient-to-br from-gray-800 to-gray-900 rounded-[2.5rem] md:rounded-[3rem] p-1.5 md:p-2 shadow-2xl green-glow">
                <div className="w-full h-full bg-white rounded-[2rem] md:rounded-[2.5rem] overflow-hidden relative">
                  {/* Status Bar */}
                  <div className="bg-gray-50 px-4 py-2 flex justify-between items-center">
                    <span className="text-xs text-gray-600">9:41</span>
                    <div className="flex items-center gap-1">
                      <div className="w-4 h-2 border border-primary rounded-sm">
                        <div className="w-3/4 h-full bg-primary rounded-sm"></div>
                      </div>
                    </div>
                  </div>

                  {/* App Header */}
                  <div className="bg-gradient-primary p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <img src="/logo.png" alt="SMR Logo" className="w-8 h-8 rounded-lg" />
                        <span className="text-white font-bold text-lg">SMR Online</span>
                      </div>
                      <div className="text-white/80 text-xl">🎲</div>
                    </div>
                  </div>

                  {/* App Content */}
                  <div className="p-4 space-y-4 bg-gray-50">
                    {/* Balance Card */}
                    <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                      <div className="text-gray-500 text-sm mb-1">Wallet Balance</div>
                      <div className="text-2xl font-bold text-gray-900">₹5,000</div>
                    </div>

                    {/* Quick Actions */}
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { icon: '📊', label: 'Results' },
                        { icon: '💰', label: 'Play' },
                        { icon: '🏆', label: 'Win' }
                      ].map((item, index) => (
                        <div key={index} className="bg-white rounded-xl p-3 text-center shadow-sm border border-gray-100">
                          <div className="text-2xl mb-1">{item.icon}</div>
                          <div className="text-xs text-gray-500">{item.label}</div>
                        </div>
                      ))}
                    </div>

                    {/* Live Results */}
                    <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-gray-900 font-semibold">Live Results</span>
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                          <span className="text-xs text-green-500">LIVE</span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        {['Milan Day', 'Rajdhani Night'].map((market, index) => (
                          <div key={index} className="flex justify-between items-center bg-gray-50 rounded-lg p-2">
                            <span className="text-sm text-gray-600">{market}</span>
                            <span className="text-primary font-bold">***-**-***</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Elements */}
              <motion.div
                className="absolute -right-4 top-20 bg-white px-3 py-2 rounded-xl shadow-lg border border-gray-100"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  <span className="text-sm text-gray-700">4.8 Rating</span>
                </div>
              </motion.div>

              <motion.div
                className="absolute -left-4 bottom-32 bg-white px-3 py-2 rounded-xl shadow-lg border border-gray-100"
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
              >
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-primary" />
                  <span className="text-sm text-gray-700">Fast Payout</span>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
